import { action, computed, observable } from 'mobx';
import { BaseStore } from '~web/store/base/base-store';

export enum ActiveAuthStep {
  NONE,
  LOGIN,
  FORGOT_PASSWORD,
  SIGNUP,
}

/**
 * Enum to give more information about where the auth is called from
 */
export enum AuthContext {
  FROM_ACCEPT_TOKEN,
  FROM_CHECKOUT,
  FROM_VERIFY_EMAIL,
  FROM_REDIRECT_TO_DISCOVER,
  FROM_DELETE_ACCOUNT,
}

/**
 * This store is used to start/finish the login, signup, and forgot password processes.
 */
export class AuthFlowStore extends BaseStore {
  @observable activeAuthStep: ActiveAuthStep;
  private context: AuthContext;
  private prefilledEmail: string;
  private emailVerificationToken: string;
  private onAuthenticationCancelled: () => void;

  constructor() {
    super();
    this.activeAuthStep = ActiveAuthStep.NONE;
    this.prefilledEmail = undefined;
    this.context = undefined;
    this.emailVerificationToken = undefined;
  }

  @action
  startSignup(
    prefilledEmail?: string,
    context?: AuthContext,
    onAuthenticationCancelled?: () => void,
    emailVerificationToken?: string,
  ): void {
    this.setData(prefilledEmail, context, onAuthenticationCancelled, emailVerificationToken);
    this.activeAuthStep = ActiveAuthStep.SIGNUP;
  }

  @action
  switchToSignup(prefilledEmail?: string): void {
    if (prefilledEmail) {
      this.prefilledEmail = prefilledEmail;
    }
    this.activeAuthStep = ActiveAuthStep.SIGNUP;
  }

  @action
  startLogin(
    prefilledEmail?: string,
    context?: AuthContext,
    onAuthenticationCancelled?: () => void,
  ): void {
    this.setData(prefilledEmail, context, onAuthenticationCancelled);
    this.activeAuthStep = ActiveAuthStep.LOGIN;
  }

  @action
  switchToLogin(prefilledEmail?: string): void {
    if (prefilledEmail) {
      this.prefilledEmail = prefilledEmail;
    }
    this.activeAuthStep = ActiveAuthStep.LOGIN;
  }

  @action
  startForgotPassword(
    prefilledEmail?: string,
    context?: AuthContext,
    onAuthenticationCancelled?: () => void,
  ): void {
    this.setData(prefilledEmail, context, onAuthenticationCancelled);
    this.activeAuthStep = ActiveAuthStep.FORGOT_PASSWORD;
  }

  @action
  closeAccount(): void {
    this.context = AuthContext.FROM_DELETE_ACCOUNT;
  }

  @action
  finish(): void {
    this.activeAuthStep = ActiveAuthStep.NONE;
    this.prefilledEmail = undefined;
    this.onAuthenticationCancelled = undefined;
    // don't clear context because it might be used after auth
    // it will be reset or set to a new value when the auth flow starts again
  }

  @action
  cancel(): void {
    if (this.onAuthenticationCancelled) {
      this.onAuthenticationCancelled();
    }
    this.finish();
  }

  @computed
  get loginStarted(): boolean {
    return this.activeAuthStep === ActiveAuthStep.LOGIN;
  }

  @computed
  get signupStarted(): boolean {
    return this.activeAuthStep === ActiveAuthStep.SIGNUP;
  }

  @computed
  get forgotPasswordStarted(): boolean {
    return this.activeAuthStep === ActiveAuthStep.FORGOT_PASSWORD;
  }

  getPrefilledEmail(): string {
    return this.prefilledEmail;
  }

  getEmailVerificationToken(): string {
    return this.emailVerificationToken;
  }

  getContext(): AuthContext {
    return this.context;
  }

  private setData(
    prefilledEmail: string,
    context: AuthContext,
    onAuthenticationCancelled?: () => void,
    emailVerificationToken?: string,
  ): void {
    this.prefilledEmail = prefilledEmail;
    this.context = context;
    this.onAuthenticationCancelled = onAuthenticationCancelled;
    this.emailVerificationToken = emailVerificationToken;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src332225586/src/modules/web/src/js/store/auth-flow-store.ts